import React, { FunctionComponent, ReactElement, useState } from "react";
import {
  GameCodes,
  GameTitles,
  PlayerLeaveLevel,
  RoundHistoryComponentProps,
  RoundType,
  SpinHistory,
} from "../../type";
import { AnimationPoint, PiratesTreasureGameData, SymbolPayout } from "./type";
import PiratesTreasureUtils from "./utils";

import "./pirates-treasure.scss";
import { Link } from "react-router-dom";
import Utils from "../../utils";
import { BuyFreeSpinRoundHistoryView } from "../../components";
import { useFocusSpinId } from "../../hooks";
import BigNumber from "bignumber.js";
import { useTranslator } from "../../context/TranslationContext";

const lineRatio = BigNumber(0.05);
export const PiratesTreasureRoundHistoryView: FunctionComponent<
  RoundHistoryComponentProps
> = ({ type, id, roundHistory }) => {
  const focusSpinId = useFocusSpinId(type, id);
  const translator = useTranslator();
  const spin =
    roundHistory.spins.find((s) => s.id === focusSpinId) ??
    roundHistory.spins[0];

  if (!spin) return <>Spin not found</>;

  if (!spin.focus) spin.focus = true;

  const plays = roundHistory.spins.reduce((ps, s) => {
    if (!ps[s.playRefNumber]) ps[s.playRefNumber] = [];
    ps[s.playRefNumber].push(s);
    return ps;
  }, {} as { [k: string]: SpinHistory[] });
  return (
    <BuyFreeSpinRoundHistoryView
      gameCode={GameCodes.PiratesTreasure}
      roundHistory={roundHistory}
    >
      <div className="card mb-3">
        <div className="card-body">
          <div className="card-title fs-4 fw-bold">
            {translator.get("spin")}
            <button
              type="button"
              className="btn btn-info float-end"
              data-bs-toggle="modal"
              data-bs-target="#explaination"
            >
              <i className="bi bi-info-circle fw-bold"></i>
            </button>
          </div>

          <hr />
          {Object.values(plays).map((s) => (
            <PlayRecord
              key={s[0].playRefNumber}
              spins={s}
              betAmount={+roundHistory.betAmount}
              onSpinClicked={(spinId) => {
                spin.focus = false;
              }}
              buyFreeSpinRate={
                roundHistory.buyFreeSpin ? roundHistory.buyFreeSpinRate : 1
              }
              roundType={roundHistory.roundType}
            />
          ))}
        </div>
      </div>
      <div id="explaination" className={`modal fade`} tabIndex={-1}>
        <div className="modal-dialog">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">{translator.get("explanation")}</h5>
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="modal"
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <div className="pb-3 fs-5">{translator.get("formula")}</div>
              <div className="pb-3">
                {translator.get("bet_size")} = {translator.get("bet")} x 0.05
              </div>
              <div className="pb-3">
                {translator.get("payout")} = {translator.get("bet_size")} x{" "}
                {translator.get("count")} x {translator.get("multiplier")}
              </div>
              <hr />
              <div className="pb-3 fs-5">{translator.get("multiplier")}</div>
              <table className="w-100">
                <tbody>
                  <tr>
                    {[
                      [201, 15],
                      [202, 8],
                      [203, 6],
                      [204, 4],
                    ].map(([symbol, payout], i) => (
                      <td key={`explanation-${symbol}`} className="text-center">
                        <div className="d-inline-block symbol">
                          {PiratesTreasureUtils.convertSymbol(+symbol)}
                        </div>
                        <div className="">{payout}</div>
                      </td>
                    ))}
                  </tr>
                  <tr>
                    {[
                      [101, 3],
                      [102, 2],
                      [103, 1],
                      [104, 1],
                    ].map(([symbol, payout], i) => (
                      <td key={`explanation-${symbol}`} className="text-center">
                        <div className="d-inline-block symbol">
                          {PiratesTreasureUtils.convertSymbol(+symbol)}
                        </div>
                        <div className="">{payout}</div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </BuyFreeSpinRoundHistoryView>
  );
};
const PlayRecord: FunctionComponent<{
  spins: SpinHistory[];
  betAmount: number;
  onSpinClicked: (id: string) => void;
  buyFreeSpinRate: number;
  roundType: RoundType;
}> = ({
  spins,
  betAmount: rawBetAmount,
  onSpinClicked,
  buyFreeSpinRate: rawBuyFreeSpinRate,
  roundType,
}) => {
  const translator = useTranslator();
  const betAmount = BigNumber(rawBetAmount).dividedBy(rawBuyFreeSpinRate);
  const firstSpin = spins[0];
  const payout = spins.reduce((t, s) => t.plus(+s.winAmount), BigNumber(0));
  const elements = [
    <div
      key={`play-${firstSpin.playRefNumber}-id`}
      className="list-group-item list-group-item-primary text-center border fs-4"
    >
      {firstSpin.mode === "Base"
        ? translator.get("normal_spin_id")
        : translator.get("free_spin_id")}
      : <span className="fw-bold">{firstSpin.playRefNumber}</span>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-header`}
      className="list-group-item list-group-item-secondary spin-table"
    >
      <div className="d-flex text-center">
        <div className="col-4 border">{translator.get("spin_count")}</div>
        <div className="col-2 border">{translator.get("bet_size")}</div>
        <div className="col-3 border">{translator.get("payout")}</div>
        <div className="col-3 border">{translator.get("profit")}</div>
      </div>
    </div>,
    <div
      key={`play-${firstSpin.playRefNumber}-content`}
      className="list-group-item text-center border border"
    >
      <div className="d-flex">
        <div className="col-4 border">{spins.length}</div>
        <div className="col-2 border">
          {Utils.asCurrency(betAmount.multipliedBy(lineRatio), 2)}
        </div>
        <div className="col-3 border">{Utils.asCurrency(payout)}</div>
        <div className="col-3 border">
          {Utils.asCurrency(
            firstSpin.reelMatrix.length > 1 &&
              firstSpin.mode === "Base" &&
              roundType !== RoundType.FreePlay
              ? payout.minus(betAmount)
              : payout,
            2
          )}
        </div>
      </div>
    </div>,
  ] as ReactElement[];
  // ignore last one
  spins.forEach((s) => {
    if (s.reelMatrix.length !== 0) {
      elements.push(
        <SpinRecord key={s.id} spin={s} onSpinClicked={onSpinClicked} />
      );
    }
  });
  return <div className="mb-2">{elements}</div>;
};

export const SpinRecord: FunctionComponent<{
  spin: SpinHistory;
  onSpinClicked: (id: string) => void;
}> = ({ spin, onSpinClicked }) => {
  const translator = useTranslator();
  const gameData = (spin.gameData as PiratesTreasureGameData) ?? {};
  const header = (
    <>
      <colgroup>
        <col width="30%" />
        <col width="15%" />
        <col width="15%" />
        <col width="15%" />
        <col width="30%" />
      </colgroup>
      <thead>
        <tr className="text-center list-group-item-secondary">
          <td className="">{translator.get("symbol")}</td>
          <td className="">{translator.get("bonus")}</td>
          <td className="">{translator.get("count")}</td>
          <td className="">{translator.get("multiplier")}</td>
          <td className="">{translator.get("payout")}</td>
        </tr>
      </thead>
    </>
  );

  let payoutTableRows: ReactElement[] = [];
  if (!spin.payout || spin.payout.length === 0) {
    if (spin.level === PlayerLeaveLevel) {
      payoutTableRows.push(
        <tr key={`payout-${spin.id}-trigger-symbol`}>
          <td colSpan={999}></td>
        </tr>
      );
    } else if (spin.reelMatrix.length > 0) {
      payoutTableRows.push(
        <tr key={`payout-${spin.id}-trigger-symbol`}>
          <td colSpan={999}></td>
        </tr>
      );
    }
  }
  payoutTableRows.push(
    ...spin.payout
      .sort((a, b) => a.symbolId - b.symbolId)
      .map((p, i) => {
        const { symbolId, featureId } = PiratesTreasureUtils.cleanSymbolId(
          p.symbolId
        );
        return (
          <tr key={`payout-${spin.id}-${i}`}>
            <td>
              <div className="symbol flex">
                {symbolId == 300
                  ? MultiplierCell({ multiplier: gameData.totalMultiplier })
                  : PiratesTreasureUtils.convertSymbol(symbolId)}
              </div>
              {featureId ? (
                <div className="symbol flex">
                  {PiratesTreasureUtils.convertSymbol(featureId)}
                </div>
              ) : (
                ""
              )}
            </td>
            <td>{symbolId == 300 ? 1 : p.multiplier}</td>
            <td>{symbolId == 300 ? 1 : p.count}</td>
            <td>{p.payout}</td>
            <td>{Utils.asCurrency(p.win)}</td>
          </tr>
        );
      })
  );
  // gameData.multiplys
  if (spin.payout.length > 0) {
    const total = spin.payout.reduce(
      (o, c) => {
        return { count: o.count + c.count, win: o.win.plus(c.win) };
      },
      { count: 0, win: BigNumber(0) }
    );
    payoutTableRows.push(
      <tr key={`payout-${spin.id}-hr`}>
        <td className="px-4" colSpan={999}>
          <hr />
        </td>
      </tr>
    );
    payoutTableRows.push(
      <tr key={`payout-${spin.id}-total`} className="fw-bold fs-6">
        <td>{translator.get("win")}:</td>
        <td colSpan={3}></td>
        <td>{Utils.asCurrency(+spin.winAmount, 2)}</td>
      </tr>
    );
  }
  let content = (
    <div className="d-flex flex-wrap text-center ">
      <div className="col-12 col-md-6  py-3">
        <Reel
          reelMatrix={spin.reelMatrix}
          points={gameData.animationPoints ?? []}
          multiplys={gameData.symbolPayouts}
          spin={spin}
        />
      </div>

      <div
        className={`col-12 col-md-6 pb-3 ${
          spin.focus ? " list-group-item-success" : ""
        }`}
      >
        <table width={"100%"}>
          {header}
          <tbody>{payoutTableRows}</tbody>
        </table>
      </div>
    </div>
  );

  const linkBonusTitle = (
    <div className="list-group-item list-group-item-primary text-center fs-5">
      {translator.get("bonus")}
    </div>
  );
  if (!spin.reelMatrix || spin.reelMatrix.length === 0) {
    content = (
      <div className={`col-12 pb-3 text-center`}>
        <table width={"100%"}>
          <tbody>{payoutTableRows} </tbody>
        </table>
      </div>
    );
  }
  console.log(spin);
  let isShowingLinkBonus = false;
  if (
    (spin.gameData.category === "BuyFreeSpin" && spin.level !== 0) ||
    (spin.gameData.category === "Normal" && spin.mode === "Free")
  ) {
    isShowingLinkBonus = true;
  }
  return (
    <Link
      to={{ hash: spin.id }}
      id={spin.id}
      type="button"
      onClick={() => onSpinClicked(spin.id)}
      key={`spin-${spin.id}-id`}
      className={`list-group-item list-group-item-action border${
        spin.focus ? " border-success" : ""
      }`}
    >
      {isShowingLinkBonus ? linkBonusTitle : ""}

      {content}
    </Link>
  );
};

export const Reel: FunctionComponent<{
  reelMatrix: number[][];
  multiplys: SymbolPayout[];
  points: AnimationPoint[];
  spin: SpinHistory;
}> = ({ reelMatrix, points, multiplys, spin }) => {
  const animatiopnMap = (points || []).reduce((map, point) => {
    const key = `${point.x}-${point.y}`;
    map[key] = point;
    return map;
  }, {} as Record<string, AnimationPoint>);

  const reelColumn = reelMatrix.reduce((col, rawCol, x) => {
    const colElements = rawCol.reduce((row, rawSymbol, y) => {
      const key = `${x}-${y}`;
      const animationPoint = animatiopnMap[key];

      var m = -1;
      if (rawSymbol === 300) {
        const multiply = multiplys.find((a) => a.x == x && a.y == y);

        if (multiply != null) {
          m = multiply.value;
        }
      }

      row.push(
        <BoardCell
          key={key}
          symbol={rawSymbol}
          point={animationPoint}
          multiplier={m}
          mode={spin.mode}
        />
      );
      return row;
    }, [] as ReactElement[]);
    col.push(
      <div key={"col-" + x} className="d-flex flex-column">
        {colElements}
      </div>
    );
    return col;
  }, [] as ReactElement[]);

  return <div className="reel d-flex justify-content-center">{reelColumn}</div>;
};

const BoardCell: FunctionComponent<{
  symbol: number;
  point?: AnimationPoint;
  multiplier: number;
  mode: string;
}> = ({ symbol, point, multiplier, mode }) => {
  const classes = ["symbol", "flex", "border", "border-dark"];
  const { symbolId, featureId } = PiratesTreasureUtils.cleanSymbolId(symbol);
  let cellContent = PiratesTreasureUtils.convertSymbol(symbolId);

  if (cellContent === 300) {
    cellContent = MultiplierCell({ multiplier: multiplier });
  }

  if (point && mode !== "Free") {
    if (point.win !== 0) {
      classes.push("animation-end");
    } else {
      classes.push("animation");
    }
  }

  return <div className={classes.join(" ")}>{cellContent}</div>;
};

const GetMultiplierImg = (multiplier: number) => {
  const m = multiplier.toString();
  const result = [];
  if (m.length === 1) {
    result.push({
      position: "absolute" as "absolute",
      top: "50%",
      left: "50%",
      transform: "translate(-50%, -50%)", // Center the image both horizontally and vertically
      height: "50%",
      width: "50%",
      backgroundImage: `url("/pt/multiplier/${m[0]}.png")`,
      backgroundSize: "contain",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      zIndex: 2,
    });
  } else {
    result.push({
      position: "absolute" as "absolute",
      top: "50%",
      left: "43%", // Positioned slightly to the left
      transform: "translate(-50%, -50%)",
      height: "40%", // Smaller size for imageStyle2
      width: "40%",
      backgroundImage: `url("/pt/multiplier/${m[0]}.png")`,
      backgroundSize: "contain",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      zIndex: 2,
    });
    result.push({
      position: "absolute" as "absolute",
      top: "50%",
      left: "63%", // Positioned slightly to the left
      transform: "translate(-50%, -50%)",
      height: "40%", // Smaller size for imageStyle2
      width: "40%",
      backgroundImage: `url("/pt/multiplier/${m[1]}.png")`,
      backgroundSize: "contain",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      zIndex: 2,
    });
  }
  return result;
};
const MultiplierCell: FunctionComponent<{ multiplier: number }> = ({
  multiplier,
}) => {
  const containerStyle = {
    height: "100%",
    width: "100%",
    color: "black",
    position: "relative" as "relative",
  };

  const imageStyle1 = {
    position: "absolute" as "absolute",
    top: 0,
    left: 0,
    height: "100%",
    width: "100%",
    backgroundImage: 'url("/pt/multiplier.png")',
    backgroundSize: "cover",
    backgroundPosition: "center",
    zIndex: 1,
  };

  const multiplierImg = GetMultiplierImg(multiplier);

  return (
    <div style={containerStyle}>
      <div style={imageStyle1}></div>
      {multiplierImg.map((a, i) => (
        <div key={i} style={a}></div>
      ))}
    </div>
  );
};
